import { useEffect, useMemo } from 'react';

import {
  OfferRedemptionType,
  useLoyaltyNotificationsRedeemableQuery,
} from 'generated/graphql-gateway';
import { LoyaltyDashboardWidgets } from 'pages/loyalty/loyalty-dashboard/types';
import { useFeaturesLoyaltyDashboard } from 'pages/loyalty/loyalty-dashboard/use-feature-loyalty-dashboard';
import { hasComponentKey } from 'pages/loyalty/loyalty-widget-components';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';

import { LoyaltyNotifications, NotificationDetails, RedeemableItems } from './types';

const MILLISECONDS_IN_HOUR = 3600000;

export const useLoyaltyNotifications: LoyaltyNotifications = (
  loyaltyUser,
  loyaltyOffersEnabled
) => {
  const { serviceMode: nullableServiceMode } = useServiceModeContext();
  const { store } = useStoreContext();

  const serviceMode = nullableServiceMode || undefined;
  const storeId = store?.number || undefined;

  const { data, loading: loadingDashboard } = useFeaturesLoyaltyDashboard();
  const widget = data?.widgets?.find(
    record =>
      record &&
      hasComponentKey<typeof record>(record) &&
      record.componentKey === LoyaltyDashboardWidgets.Notifications
  );
  const loyaltyId = loyaltyUser?.id;

  // Get all of user's redeemable notification items
  const {
    data: redeemableData,
    loading: loadingUserData,
    refetch,
  } = useLoyaltyNotificationsRedeemableQuery({
    fetchPolicy: 'cache-and-network',
    skip: !loyaltyId || !widget,
    variables: {
      rewardsInput: { ignorePointBalance: false, serviceMode, storeId },
      offersInput: { global: true, serviceMode, storeId },
      loyaltyId: loyaltyId || '',
    },
  });

  // Check if notifications are redeemable on every hour
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const scheduleNextRefetch = () => {
      const millisecondsTilNextHour =
        MILLISECONDS_IN_HOUR - (new Date().getTime() % MILLISECONDS_IN_HOUR);
      timeout = global.setTimeout(() => {
        refetchAndSchedule();
      }, millisecondsTilNextHour);
    };
    const refetchAndSchedule = () => {
      scheduleNextRefetch();
      refetch();
    };
    if (loyaltyId) {
      scheduleNextRefetch();
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [refetch, loyaltyId]);

  const redeemableLoyaltyIds = useMemo(() => {
    if (!loyaltyId || !redeemableData?.loyaltyUser) {
      return [];
    }
    const {
      loyaltyUser: { rewards, offers, promotions },
    } = redeemableData;
    let results: string[] = [];
    const addToResults = (records: RedeemableItems) => {
      if (!records) {
        return;
      }
      records.forEach(record => {
        if (record) {
          results.push(record.id);
        }
      });
    };
    addToResults(rewards);
    addToResults(offers);
    addToResults(promotions);
    return results;
  }, [loyaltyId, redeemableData]);

  const redeemableNotifications = useMemo(() => {
    if (!widget || widget.__typename !== 'LoyaltyNotificationsWidget') {
      return [];
    }

    const notifications = widget.notifications || [];
    return notifications.reduce<NotificationDetails[]>((results, notification) => {
      if (
        !notification?.loyaltyEngineId ||
        !notification.notificationDetails?.enabled ||
        (!loyaltyOffersEnabled &&
          notification?.__typename === 'SystemwideOffer' &&
          notification?.redemptionType === OfferRedemptionType.SURPRISE)
      ) {
        return results;
      }

      const { loyaltyEngineId, notificationDetails: details } = notification;
      if (redeemableLoyaltyIds.indexOf(loyaltyEngineId) >= 0) {
        return results.concat({ ...details, id: loyaltyEngineId });
      }

      return results;
    }, []);
  }, [widget, loyaltyOffersEnabled, redeemableLoyaltyIds]);

  return {
    notifications: redeemableNotifications,
    loadingNotifications: !redeemableData && (loadingDashboard || loadingUserData),
  };
};
